export const parseQuestionData = (question = {}) => {
  let raw = {};
  try {
    raw = question.data ? JSON.parse(question.data) || {} : {};
  } catch {
    raw = {};
  }

  const optionCandidates = [
    Array.isArray(raw.option) ? raw.option : undefined,
    Array.isArray(question.options) ? question.options : undefined,
    Array.isArray(question.option) ? question.option : undefined,
  ].find(Boolean);

  const fallbackOptions = ['A', 'B', 'C', 'D'].map((code) => ({ code, desc: '' }));
  const baseOptions = optionCandidates && optionCandidates.length ? optionCandidates : fallbackOptions;
  const optionsWithImages = baseOptions.map((opt) => {
    if (!opt || !opt.code) return opt;
    const desc = opt.desc || opt.text || '';
    const key = `option${opt.code}Images`;
    let images = opt[key] || question[key] || raw[key];

    if (typeof images === 'string') {
      try {
        images = JSON.parse(images);
      } catch {
        images = [];
      }
    }

    return {
      ...opt,
      desc,
      [key]: Array.isArray(images) ? images : [],
    };
  });

  const options = optionsWithImages.filter((opt) => {
    if (!opt) return false;
    const hasDesc = opt.desc && opt.desc.trim() !== '';
    const imageKey = `option${opt.code}Images`;
    const hasImages = Array.isArray(opt[imageKey]) && opt[imageKey].length > 0;
    return hasDesc || hasImages;
  });

  const optionAnswerKey = options
    .filter((opt) => opt?.anskey === true || opt?.ansKey === true || opt?.isCorrect === true || opt?.anskey === 'true')
    .map((opt) => opt.code)
    .join('|');

  const keysToCheck = ['answerKey', 'answerkey', 'anskey', 'ansKey'];
  const answerKeyCandidates = [
    optionAnswerKey,
    ...keysToCheck.map(k => question[k]),
    ...keysToCheck.map(k => raw[k])
  ].filter(Boolean);

  return {
    raw,
    options,
    text: question.text || question.questionText || raw.text || raw.questionText || '',
    description: question.description || raw.description || '',
    hint: question.hint || raw.hint || '',
    type: question.type || raw.type || raw.questionType || (String(answerKeyCandidates[0] || '').includes('|') ? 'MCQM' : 'MCQ'),
    probable: typeof raw.probable === 'number'
      ? raw.probable
      : typeof question.probable === 'number'
        ? question.probable
        : 1,
    answerKey: String(answerKeyCandidates[0] || ''),
  };
};

export const buildQuestionUpdateFormData = (question, overrides = {}) => {
  if (!question?.id) throw new Error('Missing question identifiers');

  const {
    questionText = question.questionText || question.text || '',
    answerText = question.answerText || question.answer || '',
    isActive = question.isActive,
    hint = question.hint || '',
    type,
    probable,
    answerKey,
    options,
    monthYears,
    images = [],
    dataExtras = {},
  } = overrides;

  const { raw: parsedRaw } = parseQuestionData(question);

  const normalizedOptions = Array.isArray(options)
    ? options.map((opt) => {
      const { anskey, isCorrect, ...rest } = opt;
      return rest;
    })
    : [];

  const dataPayload = {
    ...parsedRaw,
    ...dataExtras,
    ...(type !== undefined ? { type } : {}),
    ...(probable !== undefined ? { probable: Number(probable) || 0 } : {}),
    ...(answerKey !== undefined ? { answerKey } : {}),
    ...(options !== undefined ? { option: normalizedOptions } : {}),
  };

  const formData = new FormData();
  formData.append('id', question.id);
  if (question.uuid) formData.append('uuid', question.uuid);
  formData.append('questionText', questionText);
  formData.append('answerText', answerText || '');
  formData.append('isActive', isActive ? 'true' : 'false');
  if (hint) formData.append('hint', hint);
  if (type !== undefined) formData.append('type', type);
  if (probable !== undefined) formData.append('probable', Number(probable) || 0);
  if (answerKey) formData.append('answerKey', answerKey);
  formData.append('data', JSON.stringify(dataPayload));

  const monthEntries = Array.isArray(monthYears) && monthYears.length
    ? monthYears
    : Array.isArray(question.monthYears)
      ? question.monthYears
      : [];

  monthEntries.forEach((entry) => {
    if (entry?.month) formData.append('month', entry.month);
    if (entry?.year) formData.append('year', entry.year);
  });

  images.forEach((file) => formData.append('images', file));

  return formData;
};
